import { useEffect, useState, type FC } from 'react'
import { ThumbUpIcon, PeopleIcon, ChevronLeftIcon, ChevronRightIcon, PrintIcon,InfoIcon, CalendarXIcon, SlashCircleIcon } from './Icons';
import type { Reservation, ReservationStatus } from './reservations.types';
import { ThumbsDownIcon } from 'lucide-react';
import { useAuth } from '../Context/AuthContext';
import { getDashboard, type ReservationDashboardResponse } from '../lib/api/reservations.apis';
import { useParams } from 'react-router-dom';
import Spinner from './Components/Spinner';
function addDays(d: Date, n: number): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + n);
  return copy;
}

function formatDateLong(d: Date): string {
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

function formatDate(iso: string, tz: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric", timeZone: tz,
  });
}

function formatTime(iso: string, tz: string): string {
  return new Date(iso).toLocaleTimeString("en-GB", {
    hour: "2-digit", minute: "2-digit", timeZone: tz,
  });
}
// ─── ILLUSTRATIONS ─────────────────────────────────────────────────────────────
const ClockIllustration: FC = () => (
  <svg width="150" height="150" viewBox="0 0 140 140" fill="none">
    <ellipse cx="70" cy="110" rx="45" ry="8" fill="#e2e8f0" opacity="0.6"/>
    <ellipse cx="55" cy="108" rx="14" ry="5" fill="#fbbf24" opacity="0.7"/>
    <ellipse cx="85" cy="108" rx="10" ry="4" fill="#fbbf24" opacity="0.5"/>
    <ellipse cx="70" cy="112" rx="18" ry="6" fill="#f59e0b" opacity="0.6"/>
    <circle cx="70" cy="60" r="42" fill="#dbeafe" stroke="#93c5fd" strokeWidth="2"/>
    <circle cx="70" cy="60" r="36" fill="white" stroke="#bfdbfe" strokeWidth="1.5"/>
    {[0,30,60,90,120,150,180,210,240,270,300,330].map((deg, i) => {
      const rad = (deg - 90) * Math.PI / 180;
      const x1 = 70 + 30 * Math.cos(rad), y1 = 60 + 30 * Math.sin(rad);
      const x2 = 70 + 33 * Math.cos(rad), y2 = 60 + 33 * Math.sin(rad);
      return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#93c5fd" strokeWidth={i % 3 === 0 ? 2 : 1}/>;
    })}
    <line x1="70" y1="60" x2="70" y2="36" stroke="#f97316" strokeWidth="3" strokeLinecap="round"/>
    <line x1="70" y1="60" x2="88" y2="68" stroke="#1e40af" strokeWidth="2.5" strokeLinecap="round"/>
    <circle cx="70" cy="60" r="3" fill="#f97316"/>
    <circle cx="30" cy="28" r="2" fill="#f9a8d4"/>
    <circle cx="112" cy="20" r="2.5" fill="#93c5fd"/>
    <circle cx="118" cy="75" r="1.5" fill="#a5f3fc"/>
    <line x1="28" y1="50" x2="32" y2="50" stroke="#f9a8d4" strokeWidth="1.5"/>
    <line x1="30" y1="48" x2="30" y2="52" stroke="#f9a8d4" strokeWidth="1.5"/>
  </svg>
);

const ReviewIllustration: FC = () => (
  <svg width="120" height="100" viewBox="0 0 120 100" fill="none">
    <rect x="25" y="10" width="50" height="80" rx="8" fill="#dbeafe" stroke="#93c5fd" strokeWidth="2"/>
    <rect x="30" y="18" width="40" height="64" rx="4" fill="white"/>
    <circle cx="50" cy="15" r="2" fill="#93c5fd"/>
    <rect x="55" y="5" width="45" height="32" rx="10" fill="#fce7f3" stroke="#f9a8d4" strokeWidth="1.5"/>
    <circle cx="68" cy="21" r="3" fill="#f472b6"/>
    <circle cx="78" cy="21" r="3" fill="#f472b6" opacity="0.6"/>
    <circle cx="88" cy="21" r="3" fill="#f472b6" opacity="0.3"/>
    <path d="M62 37 L68 37 L65 45 Z" fill="#fce7f3"/>
    <path d="M50 35 L52 40 L57 40 L53 43 L55 48 L50 45 L45 48 L47 43 L43 40 L48 40 Z" fill="#fbbf24" opacity="0.8"/>
    <line x1="35" y1="55" x2="65" y2="55" stroke="#e2e8f0" strokeWidth="2" strokeLinecap="round"/>
    <line x1="35" y1="62" x2="58" y2="62" stroke="#e2e8f0" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="18" cy="60" r="2" fill="#f9a8d4"/>
    <path d="M15 30 L16.5 33 L15 36 L18 34.5 L21 36 L19.5 33 L21 30 L18 31.5 Z" fill="#f9a8d4" opacity="0.7"/>
  </svg>
);


const DashboardTab: FC<{ onAddBooking: () => void; STATUS_CONFIG: any; setActiveTab: (tab: string) => void; }> = ({ onAddBooking, STATUS_CONFIG, setActiveTab }) => {
    const [dashBoardData, setDashBoardData] = useState<ReservationDashboardResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const today = new Date();
    const [currentDate, setCurrentDate] = useState(today);

    const restaurantId = useParams().restaurantId!;
    const { user, accessToken } = useAuth();

    
    const fetchDashboard = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getDashboard(restaurantId, {
              token: accessToken,
              date: currentDate.toISOString().slice(0, 10),
            });
            // console.log("Fetched dashboard data:", data);
            setDashBoardData(data);
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
            setError(error instanceof Error ? error.message : "Failed to fetch dashboard data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // if (!restaurantId || !accessToken) return;
        fetchDashboard();
    }, [restaurantId, currentDate]);


  const stats = [
    { label: "Confirmed Bookings",   value: dashBoardData?.metrics?.confirmedBookings || 0, Icon: ThumbUpIcon   },
    { label: "Total Covers",         value: dashBoardData?.metrics?.totalCovers || 0, Icon: PeopleIcon    },
    { label: "Cancelled Bookings",   value: dashBoardData?.metrics?.cancelledBookings || 0, Icon: CalendarXIcon },
    { label: "No Shows", value: dashBoardData?.metrics?.noShows || 0, Icon: ThumbsDownIcon },
  ];

  return (
    <div className="p-4 md:p-8 bg-slate-100 min-h-full">
      {/* Greeting */}
      <div className="mb-6">
        <h1 className="m-0 text-3xl font-extrabold text-slate-900">Hello {user?.name?.split(" ")[0] || "there"}! 👋</h1>
        <p className="m-0 mt-1 text-sm text-slate-500">Here is a real-time snapshot of your restaurant's operations.</p>
      </div>

      {/* Date nav + action buttons */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center overflow-hidden rounded-xl border-2 border-slate-800">
          <button onClick={() => setCurrentDate(d => addDays(d, -1))}
            className="bg-slate-800 hover:bg-slate-700 text-white border-none cursor-pointer px-4 py-2.5 flex items-center transition-colors">
            <ChevronLeftIcon />
          </button>
          <span className="bg-slate-800 text-white px-8 py-2.5 font-bold text-sm whitespace-nowrap select-none">
            {formatDateLong(currentDate)}
          </span>
          <button onClick={() => setCurrentDate(d => addDays(d, 1))}
            className="bg-slate-800 hover:bg-slate-700 text-white border-none cursor-pointer px-4 py-2.5 flex items-center transition-colors">
            <ChevronRightIcon />
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {["Guest list", "Day View"].map(label => (
            <button key={label}
              className="bg-slate-800 hover:bg-slate-700 text-white border-none rounded-xl px-4 py-2.5 text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap">
              {label}
            </button>
          ))}
          <button className="bg-slate-800 hover:bg-slate-700 text-white border-none rounded-xl px-3 py-2.5 flex items-center cursor-pointer transition-colors">
            <PrintIcon />
          </button>
        </div>
      </div>

      {/* Stats strip */}
      {loading ? (
        <div className="rounded-2xl grid grid-cols-2 md:grid-cols-4 mb-6 shadow-sm overflow-hidden">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={`bg-white px-6 py-5 flex items-start gap-4 ${i < 3 ? "border-r border-slate-100" : ""}`}>
              <div className="w-6 h-6 rounded-md bg-slate-200 animate-pulse flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="h-4 w-24 bg-slate-200 rounded animate-pulse mb-2" />
                <div className="h-9 w-16 bg-slate-200 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl grid grid-cols-2 md:grid-cols-4 mb-6 shadow-sm overflow-hidden">
          {stats.map(({ label, value, Icon }, i) => (
            <div key={i} className={`bg-white px-6 py-5 flex items-start gap-4 ${i < 3 ? "border-r border-slate-100" : ""}`}>
              <div className="text-slate-400 mt-0.5 flex-shrink-0"><Icon /></div>
              <div>
                <p className="m-0 text-slate-500 text-sm mb-1">{label}</p>
                <p className="m-0 text-4xl font-extrabold text-slate-900 leading-none">{value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Two-column bottom */}
      <div className="grid grid-cols-1 gap-5">
        {/* Left column */}
        {/* <div className="flex flex-col gap-5">
          {/* Latest Reviews 
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden flex-1">
            <div className="px-6 py-4 border-b border-slate-50">
              <h3 className="m-0 text-base font-bold text-slate-900">Latest Reviews</h3>
            </div>
            <div className="px-6 py-8 flex flex-col items-center justify-center min-h-40">
              <ReviewIllustration />
              <p className="m-0 mt-3 text-sm text-slate-400">No reviews yet for this date</p>
            </div>
          </div>
        </div> */}

        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="m-0 text-sm font-bold text-slate-900 uppercase tracking-widest">Today's Overview</h3>
              <button className="text-slate-300 hover:text-slate-400 bg-transparent border-none cursor-pointer flex items-center p-0 transition-colors">
                <InfoIcon />
              </button>
            </div>
            {(dashBoardData?.todaysOverview?.bookings.length ?? 0) > 0 && (
              <span className="text-xs font-semibold text-slate-400 bg-slate-50 px-2.5 py-1 rounded-full">
                {dashBoardData?.todaysOverview?.bookings.length} booking{dashBoardData?.todaysOverview?.bookings.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {/* Body */}
          <div className="flex flex-col">
            {(dashBoardData?.todaysOverview?.bookings.length ?? 0) === 0 ? (
              <div className="px-6 py-12 flex flex-col items-center justify-center text-center">
                <ClockIllustration />
                <p className="m-0 mt-4 text-sm font-semibold text-slate-500">No bookings for today</p>
                <p className="m-0 mt-1 text-xs text-slate-400 mb-5">Add your first booking to get started</p>
                <button
                  onClick={onAddBooking}
                  className="bg-orange-500 hover:bg-orange-600 text-white border-none rounded-xl px-6 py-2.5 font-bold text-sm cursor-pointer transition-colors"
                >
                  + Add Booking
                </button>
              </div>
            ) : (
              <>
                {dashBoardData?.todaysOverview?.bookings.map((r, i) => {
                  const s = STATUS_CONFIG[r.status];
                  const tz = dashBoardData?.timezoneUsed ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
                  return (
                    <div
                      key={i}
                      className="flex items-center gap-4 px-6 py-3.5 border-b border-slate-50 last:border-0 hover:bg-slate-50 cursor-pointer transition-colors group" onClick={() => setActiveTab("bookings")}
                    >
                      {/* Time */}
                      <span className="text-xs font-bold text-slate-400 min-w-[44px] tabular-nums">
                        {formatDate(r.startTime, tz)}
                      </span>

                      <span className="text-xs font-bold text-slate-400 min-w-[44px]=">
                        At {formatTime(r.startTime, tz)}
                      </span>

                      {/* Divider dot */}
                      <div className="w-1 h-1 rounded-full bg-slate-200 flex-shrink-0" />

                      {/* Guest */}
                      <span className="flex-1 text-sm font-semibold text-slate-800 truncate">{r.guestName}</span>

                      {/* Party size */}
                      <div className="flex items-center gap-1 text-xs text-slate-400">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
                          <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
                        </svg>
                        <span>{r.partySize}</span>
                      </div>

                      {/* Status badge */}
                      <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full tracking-wide ${s.bg} ${s.text}`}>
                        {s.label}
                      </span>

                      {/* Hover arrow */}
                      <svg
                        width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        className="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                      >
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </div>
                  );
                })}

                {/* Footer CTA */}
                <div className="px-6 py-3 border-t border-slate-50 flex items-center justify-between">
                  <button
                    onClick={onAddBooking}
                    className="text-xs font-semibold text-orange-500 hover:text-orange-600 bg-transparent border-none cursor-pointer p-0 transition-colors"
                  >
                    + Add Booking
                  </button>
                  <button className="text-xs font-semibold text-slate-400 hover:text-slate-600 bg-transparent border-none cursor-pointer p-0 transition-colors" onClick={() => setActiveTab("bookings")}>
                    View all →
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardTab

